/* eslint-disable max-statements */
/* eslint-disable no-mixed-spaces-and-tabs */
import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import React from 'react';
import { BsCheck2Circle } from 'react-icons/bs';
import {
	QueryClient,
	QueryClientProvider,
	useMutation,
	useQuery
} from 'react-query';
import axios from 'axios';
import LoadingSixImagesAnim, {
	LoadingSkeleton
} from '../../Utils/LoadingAnimations/LoadingRectanglesAnimation';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import { useDispatch } from 'react-redux';
import { SetNotificationAction } from '../../../redux/actions/notificationAction';
import { stateNotificationsEnum } from '../../Utils/notification';
import { globalEnv } from '../../../config/baseConfig';
import LazyImage from '../../Utils/Components/lazyImageComponent';
import axiosInstance from '../../../config/axiosInstance';
import SaveButton from '../../Utils/saveButton';
import placeholder from '../../../assets/images/placeholder.png';

const queryClient = new QueryClient();

type props = {
	isOpen: boolean;
	setOpen: (isOpen: boolean) => void;

	selectedTopics: any[];
	setTopics: (topics: any[]) => void;

	userId: string;
	dashboardId: string;
};

export default function TopicAddModalForDash({
	isOpen = false,
	setOpen,
	selectedTopics,
	setTopics,
	userId,
	dashboardId
}: props) {
	const cancelButtonRef = useRef(null);

	const [addedTopics, setAddedTopics] = useState<any[]>([]);
	const [removedTopics] = useState<any[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const dispatch = useDispatch();

	const fetchTopics = async () => {
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data } = await axiosInstance().get(
			`${globalEnv.BASE_URL}/user/get-my-subscribed-topics`,
			config
		);
		return data;
	};

	const { data, isLoading, isFetching, isError } = useQuery(
		['topics', userId],
		() => fetchTopics(),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			staleTime: 0,
			cacheTime: 0
		}
	);

	const addTopicsToDashboardReq = async () => {
		const dto = {
			dashboardId: dashboardId,
			topics: addedTopics.map((topic) => topic._id)
		};
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data: response } = await axiosInstance().put(
			globalEnv.BASE_URL + '/dashboard/add-topic',
			dto,
			config
		);
		return response;
	};

	const addTopicsToDash = useMutation(addTopicsToDashboardReq, {
		onSuccess: () => {
			dispatch(
				SetNotificationAction({
					message: 'Successfully added topics to dashboard',
					type: stateNotificationsEnum.SUCCESS
				})
			);
			setOpen(false);
		},
		onError: () => {
			dispatch(
				SetNotificationAction({
					message: 'An error occured!',
					type: stateNotificationsEnum.ERROR
				})
			);
			setIsSaving(false);
		}
		// onSettled: () => {
		// 	queryClient.invalidateQueries('create');
		// }
	});

	const save = () => {
		let temp = selectedTopics;
		if (removedTopics !== null)
			removedTopics.forEach((topic: any) => {
				temp = temp.filter((item) => item._id !== topic._id);
			});

		if (addedTopics !== null) temp = temp.concat(addedTopics);
		if (temp.length > 0) {
			setIsSaving(true);
			addTopicsToDash.mutate();
			setTopics(temp);
		} else {
			dispatch(
				SetNotificationAction({
					message: 'An error occured!',
					type: stateNotificationsEnum.ERROR
				})
			);
		}
	};

	useEffect(() => {
		return () => {
			console.log('Unmount!');
		};
	}, []);

	return (
		<QueryClientProvider client={queryClient}>
			{/* {isWarning && <WarnModal isWarn={isWarning} setIsWarn={setIsWarning} />} */}
			<Transition.Root
				show={isOpen}
				as={Fragment}
				enter="transition duration-100 ease-out"
				enterFrom="transform scale-95 opacity-0"
				enterTo="transform scale-100 opacity-100"
				leave="transition duration-75 ease-out"
				leaveFrom="transform scale-100 opacity-100"
				leaveTo="transform scale-95 opacity-0"
			>
				<Dialog
					as="div"
					className="fixed z-10 inset-0 pt-5"
					initialFocus={cancelButtonRef}
					onClose={setOpen}
				>
					<div className="flex items-end justify-center min-h-screen text-center sm:block sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
						</Transition.Child>

						{/* This element is to trick the browser into centering the modal contents. */}
						<span
							className="hidden sm:inline-block sm:align-middle sm:h-screen"
							aria-hidden="true"
						>
							&#8203;
						</span>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<div
								className="relative inline-block bg-white dark:bg-gray-800 rounded-lg text-left
                                       shadow-xl transform transition-all align-middle ml-20 mr-5
                                       max-w-7xl h-[85vh] overflow-hidden lg:w-10/12 w-9/12 my-10 sm:my-0"
							>
								<section className="text-gray-700 overflow-scroll h-[75vh]">
									<div className="container sm:px-2 md:px-20 mx-auto lg:pt-5 text-center">
										<h3 className="lg:text-5xl text-4xl mt-3 lg:mt-0 font-semibold text-gray-800 dark:text-white">
											Add topics
										</h3>
										<div className="border-t mt-3 mb-5 border-gray-300 dark:border-gray-700 transition-all duration-500 text-center"></div>

										<div className="flex flex-wrap -m-1 md:-m-2 w-full">
											{isLoading ? (
												<div className="flex flex-wrap w-full justify-center mt-3">
													<LoadingSixImagesAnim />
												</div>
											) : isError ? (
												<p>There was an error</p>
											) : (
												data.topics.map((item: any) => {
													if (
														!selectedTopics.some((e) => e._id === item._id) ||
														addedTopics.some((e) => e._id === item._id)
													)
														return (
															<SingleTopic
																key={item._id}
																item={item}
																addedTopics={addedTopics}
																setAddedTopics={setAddedTopics}
															/>
														);
												})
											)}
											{isSaving ||
												(isFetching && (
													<div className="flex flex-wrap w-full justify-center mt-3">
														<LoadingSixImagesAnim />
													</div>
												))}
										</div>
									</div>
								</section>
								<div className="bg-white dark:bg-gray-800 px-4 pb-3 sm:px-6 sm:flex sm:flex-row-reverse">
									{(removedTopics.length > 0 || addedTopics.length > 0) && (
										<SaveButton isSaving={isLoading} saveFunc={() => save()} />
									)}
									<button
										type="button"
										className="btn btn-outline btn-sm btn-error mr-2"
										onClick={() => setOpen(false)}
										ref={cancelButtonRef}
									>
										Cancel
									</button>
								</div>
							</div>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition.Root>
		</QueryClientProvider>
	);
}

type singleTopicProps = {
	item: any;
	addedTopics: any[];
	setAddedTopics: (item: any) => void;
};

const SingleTopic = (props: singleTopicProps) => {
	const [isSelected, setSelected] = useState(false);
	const [random, setRandom] = useState(Math.floor(Math.random() * 101));

	const updateSelect = () => {
		if (!isSelected) props.setAddedTopics([...props.addedTopics, props.item]);
		else {
			props.setAddedTopics(
				props.addedTopics.filter((item) => item._id !== props.item._id)
			);
		}
		setSelected(!isSelected);
	};

	return (
		<div
			className="flex flex-wrap flex-col w-1/3 p-4"
			onClick={() => updateSelect()}
		>
			<div className="relative rounded-lg flex justify-center content-center w-full aspect-[16/10] dark:bg-gray-600 bg-gray-300 mb-2">
				<LazyImage
					photo={
						props.item.backgroundImage.url_low_res != undefined
							? props.item.backgroundImage.url_low_res
							: placeholder
					}
					style={
						'block object-cover object-center w-full aspect-[16/10] rounded-lg'
					}
					loaderDimesnions={80}
					effect={'blur'}
				/>
				<div
					className={
						isSelected
							? 'absolute inset-0 bg-gradient-to-t rounded-lg from-black/75 opacity-100 text-center transition-colors duration-500'
							: 'absolute inset-0 bg-gradient-to-t rounded-lg from-black/75 opacity-0 hover:opacity-100 transition-all duration-500 text-center'
					}
				></div>
				{isSelected ? (
					<BsCheck2Circle
						size="120"
						className="absolute h-[50%] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-gray-100 dark:text-primary text-center transition-all duration-500 opacity-100"
					/>
				) : (
					<BsCheck2Circle
						size="120"
						className="absolute h-[50%] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-gray-100 mx-2 dark:text-primary text-center transition-all duration-500 opacity-0 scale-0"
					/>
				)}
			</div>

			<h3 className="font-medium dark:text-white">{props.item.name}</h3>
		</div>
	);
};
